import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  CheckoutAbTestMetricToOptimize,
  CheckoutAbTestMetricToOptimizeLabelMap,
} from "@redotech/redo-conversion-core-common/checkouts/checkout-ab-test-builder";
import { checkoutOptimizationTreatmentStorefrontFeaturesSchema } from "@redotech/redo-conversion-core-common/split-slices";
import { RcoSplitStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import LinkExternalIcon from "@redotech/redo-icons/link-external-02.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { CHECKOUT_OPTIMIZATION_TREATMENT_ID_QUERY_PARAM } from "@redotech/redo-web/utils/shared-conf";
import { memo, useMemo } from "react";
import { useParams } from "react-router-dom";
import { PartialWithRequiredType } from "..";
import {
  SplitVariantControls,
  SplitVariantControlsProps,
} from "../common/split-variant-controls";
import { TreatmentStorefrontFeaturesConfiguration } from "./treatment-storefront-features-configuration";

interface RcoSplitViewInspectorProps {
  step: PartialWithRequiredType<RcoSplitStep>;
  onClose?: () => void;
}

export const RcoSplitViewInspector = memo(function RcoSplitViewInspector({
  step,
  onClose,
}: RcoSplitViewInspectorProps) {
  const { t } = useTranslation();
  const teamId = useParams().teamId;
  const team = useRequiredContext(TeamContext);

  const treatments = useMemo(() => {
    return step.treatments ?? [];
  }, [step.treatments]);

  const totalWeight = useMemo(() => {
    return treatments.reduce(
      (acc, treatment) => acc + (treatment.weight || 0),
      0,
    );
  }, [treatments]);

  const isWeightValid: SplitVariantControlsProps["isWeightValid"] =
    useMemo(() => {
      const firstTreatment = treatments[0];
      if (!firstTreatment) {
        return { isValid: false, errorMessage: t("No variants found") };
      }

      const allTreatmentsHaveWeight = treatments.every(
        (treatment) =>
          treatment.weight && treatment.weight > 0 && treatment.weight <= 100,
      );
      if (!allTreatmentsHaveWeight) {
        return {
          isValid: false,
          errorMessage: t(
            "All variants must have a weight between 0% and 100%",
          ),
        };
      }

      const totalWeightIs100 = totalWeight === 100;
      if (totalWeightIs100) {
        return { isValid: true };
      }

      const allTreatmentsHaveSameWeight = treatments.every(
        (treatment) =>
          treatment.weight && treatment.weight === firstTreatment.weight,
      );
      if (allTreatmentsHaveSameWeight) {
        return { isValid: true };
      } else {
        return {
          isValid: false,
          errorMessage: t(
            "All variants must add up to 100% (or have the same weight)",
          ),
        };
      }
    }, [totalWeight, treatments, t]);

  const handleNavigateToSplitPage = useHandler(() => {
    if (!teamId) {
      return;
    }

    const url = `stores/${teamId}/conversion/ab-tests/${step.splitId}`;
    window.open(url, "_blank");
  });

  return (
    <Flex dir="column" gap="3xl" justify="flex-start">
      <Flex dir="row" justify="space-between">
        <Text fontSize="md" fontWeight="semibold">
          {t("A/B test")}
        </Text>
        <RedoButton
          aria-label={t("Close")}
          hierarchy="tertiary"
          IconLeading={XIcon}
          onClick={onClose}
          size="sm"
        />
      </Flex>
      <Flex dir="column" gap="xl" justify="flex-start">
        <Flex align="flex-start" dir="row" gap="md" justify="space-between">
          <TextWithTitle text={step.name ?? ""} title={t("Test name")} />
          <RedoButton
            aria-label={t("Open split test")}
            hierarchy="tertiary"
            IconLeading={LinkExternalIcon}
            onClick={handleNavigateToSplitPage}
            size="xs"
          />
        </Flex>
        <TextWithTitle text={step.hypothesis ?? ""} title={t("Hypothesis")} />
        <Flex dir="column" gap="xs" justify="flex-start">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Optimize for")}
          </Text>
          <RedoBadge
            color="blue"
            size="xs"
            text={
              CheckoutAbTestMetricToOptimizeLabelMap[
                step.metricToOptimize ??
                  CheckoutAbTestMetricToOptimize.CHECKOUT_CONVERSION_RATE
              ]
            }
          />
        </Flex>
        <Flex dir="column" gap="xs" justify="flex-start">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Variants")}
          </Text>
          <SplitVariantControls
            isWeightValid={isWeightValid}
            onAddVariant={() => {}}
            onBalanceWeights={() => {}}
            onRemoveVariant={() => {}}
            onVariantNameChange={() => {}}
            onVariantWeightChange={() => {}}
            readOnly
            totalWeight={totalWeight}
            variants={treatments.map((treatment) => ({
              id: treatment.treatmentId ?? "",
              name: treatment.name ?? "",
              weight: treatment.weight ?? 0,
            }))}
          />
        </Flex>
        {step.treatments?.some((treatment) => !!treatment.storefrontFeatures) &&
          step.treatments?.map((treatment) => {
            const storefrontFeaturesParseResult =
              checkoutOptimizationTreatmentStorefrontFeaturesSchema.safeParse(
                treatment.storefrontFeatures,
              );
            const storefrontFeatures = storefrontFeaturesParseResult.success
              ? storefrontFeaturesParseResult.data
              : undefined;

            const previewUrl = treatment.treatmentId
              ? `https://${team.storeUrl}?${CHECKOUT_OPTIMIZATION_TREATMENT_ID_QUERY_PARAM}=${treatment.treatmentId}&preview=true`
              : undefined;

            return (
              <TreatmentStorefrontFeaturesConfiguration
                key={treatment.treatmentId}
                previewUrl={previewUrl}
                readonly
                storefrontFeatures={storefrontFeatures}
                treatmentId={treatment.treatmentId ?? ""}
                treatmentName={treatment.name ?? ""}
              />
            );
          })}
      </Flex>
    </Flex>
  );
});

const TextWithTitle = memo(function TextWithTitle({
  title,
  text,
}: {
  title: string;
  text: string;
}) {
  return (
    <Flex dir="column" gap="xs" justify="flex-start">
      <Text fontSize="xs" fontWeight="medium" textColor="secondary">
        {title}
      </Text>
      <Text fontSize="xs" fontWeight="medium" textColor="primary">
        {text}
      </Text>
    </Flex>
  );
});
